import { create } from 'zustand';
import {
  availability,
  currentPermission,
  requestPermission,
  show,
  type NotificationAvailability,
} from './systemNotifications.js';
import { removePushSubscription, syncPushSubscription } from './push.js';

/**
 * The user's notification preference and what the browser currently allows.
 *
 * Two separate things, deliberately:
 *   - `permission` belongs to the browser. It can be revoked from the address
 *     bar at any time and the app can only read it.
 *   - `enabled` belongs to the user. Turning notifications off in the account
 *     menu must not require revoking a browser permission, and turning them back
 *     on must not re-prompt when the grant is already there.
 *
 * A zustand store rather than Context for the same reason as the auth store:
 * the notifier is called from the socket bridge, which reads the current value
 * with `getState()` rather than through a hook.
 */

const STORAGE_KEY = 'orbit-sync.notifications-enabled';
const PROMPT_KEY = 'orbit-sync.notifications-prompt-dismissed';

function readFlag(key: string): boolean | null {
  try {
    const raw = window.localStorage.getItem(key);

    return raw === null ? null : raw === 'true';
  } catch {
    // Storage disabled; the preference lasts for this tab only.
    return null;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Non-fatal - see tokenStorage for the same trade-off.
  }
}

/**
 * What came back from the last permission request.
 *
 *   - `granted` / `denied` are the browser's answer.
 *   - `dismissed` is `default` coming back from a prompt that was closed without
 *     an answer - INCLUDING Chrome's "quieter messaging", which replaces the
 *     dialog with a small bell icon in the address bar. That case looks to a
 *     user exactly like nothing happened, so it needs its own wording rather
 *     than being folded into "denied".
 */
export type RequestOutcome = 'granted' | 'denied' | 'dismissed' | null;

export interface NotificationState {
  availability: NotificationAvailability;
  permission: NotificationPermission;
  /** The user's opt-in. Only meaningful together with a granted `permission`. */
  enabled: boolean;
  /** True while the browser's permission prompt is on screen. */
  requesting: boolean;
  /** The last answer, for UI that has to explain what just happened. */
  lastOutcome: RequestOutcome;
  /** True once the user has dismissed the one-time enable banner. */
  promptDismissed: boolean;
  /** This browser is subscribed to Web Push, so the server notifies it even while closed. */
  pushActive: boolean;
  /**
   * Opts in, prompting for permission if it has not been decided.
   *
   * MUST be called from a user gesture - the browser ignores a prompt raised
   * from anywhere else. Resolves to the resulting permission so the caller can
   * explain a denial.
   */
  enable: () => Promise<NotificationPermission>;
  disable: () => void;
  /** Re-reads the browser's grant, which can change while the tab is open. */
  refresh: () => void;
  /** Hides the one-time enable banner, permanently. */
  dismissPrompt: () => void;
  /** Subscribes this browser to Web Push when notifications are on. Run once signed in. */
  syncPush: () => Promise<void>;
}

const initialAvailability = availability();
const initialPermission = currentPermission();
const storedPreference = readFlag(STORAGE_KEY);

export const useNotificationStore = create<NotificationState>((set, get) => ({
  availability: initialAvailability,
  permission: initialPermission,
  // No stored preference yet: an existing grant counts as consent already given,
  // so a returning user is not asked to opt in twice.
  enabled: storedPreference ?? initialPermission === 'granted',
  requesting: false,
  lastOutcome: null,
  promptDismissed: readFlag(PROMPT_KEY) ?? false,
  pushActive: false,

  async enable(): Promise<NotificationPermission> {
    if (availability() !== 'available') {
      set({ availability: availability() });

      return 'denied';
    }

    set({ requesting: true });

    const permission = await requestPermission();
    const enabled = permission === 'granted';

    writeFlag(STORAGE_KEY, enabled);
    set({
      permission,
      enabled,
      requesting: false,
      // `default` means the prompt was closed without an answer, or never shown.
      lastOutcome: enabled ? 'granted' : permission === 'denied' ? 'denied' : 'dismissed',
    });

    if (enabled) {
      // Immediate proof. Without it the user's next step is "wait for someone to
      // message me and hope", which is not a test anyone can run alone.
      void show({
        title: 'Orbit Sync',
        body: 'Notifications are on. This is what a new message looks like.',
        tag: 'orbit-sync:enabled',
        url: '/',
      });
      await get().syncPush();
    }

    return permission;
  },

  disable(): void {
    // The browser grant is left alone: only the user can revoke that, from the
    // address bar. This is the app-level switch.
    writeFlag(STORAGE_KEY, false);
    set({ enabled: false, lastOutcome: null, pushActive: false });
    void removePushSubscription();
  },

  refresh(): void {
    set({ availability: availability(), permission: currentPermission() });
  },

  dismissPrompt(): void {
    writeFlag(PROMPT_KEY, true);
    set({ promptDismissed: true });
  },

  async syncPush(): Promise<void> {
    const { enabled } = get();

    if (!enabled || currentPermission() !== 'granted') {
      return;
    }

    set({ pushActive: await syncPushSubscription() });
  },
}));
